import Script from 'next/script'
import { useTranslation } from 'next-i18next'
import { useContext } from 'react'
import { LocaleContext } from 'components/common/LocaleContext'
import {
  getDateForTimezone,
  getHoursForTimezone,
  getTimeForTimezone
} from 'components/common/date.utils'
import { NonNullLiveEdition } from 'services/api/database/EventService'

interface EventMicrodataProps {
  name: string
  startDate: Date | null
  endDate: Date | null
  currency: string
  city: string
  images: string[]
  price?: number | null
  slug: any
  longitude: number | null
  latitude: number | null
  organizer: string
  organizerUrl: string | null
  peyceReview: string
  races: NonNullLiveEdition['races']
  basePath: string
}
export default function Event({
  name,
  endDate,
  currency,
  longitude,
  latitude,
  slug,
  city,
  images,
  peyceReview,
  organizer,
  organizerUrl,
  races,
  basePath
}: EventMicrodataProps) {
  const { t } = useTranslation(['common', 'event'])
  const { dateStringLocale } = useContext(LocaleContext)

  const eventTitle = name.toLowerCase().includes(city.toLowerCase())
    ? name
    : t('eventPage.eventMetaTitle', { name, city })

  const idJsonObject = {
    '@context': 'http://schema.org',
    '@type': 'ItemList',
    url: `${basePath}/event/${slug}`,
    name: eventTitle,
    ...(peyceReview && {
      description: peyceReview.replace(/<[^>]+>/g, '')
    }),
    ...(images.length > 0 && {
      image: images
    }),
    itemListElement: [] as unknown[]
  }

  races.map((race, index) => {
    const raceStartDate = race.startDate

    const formattedStartDate = !raceStartDate
      ? ''
      : getDateForTimezone(raceStartDate, race.timeZone, dateStringLocale, {
          weekday: 'short',
          month: 'long',
          day: 'numeric'
        })

    const hours = !raceStartDate
      ? ''
      : getHoursForTimezone(raceStartDate, race.timeZone)
    const startTime =
      hours && raceStartDate
        ? getTimeForTimezone(raceStartDate, race.timeZone, dateStringLocale)
        : ''

    const displayStartTime = !startTime
      ? ''
      : startTime[0] === '0'
      ? startTime.slice(1)
      : startTime

    const positiveElevation =
      race.raceData?.[1]?.value && race.raceData[1].value !== '0'
        ? ` ${race.raceData[1].value} ${race.raceData[1].unit}`
        : null

    const displayElevation = positiveElevation ? `${positiveElevation} D+` : ''
    const displayDistance =
      race.raceData?.[0]?.value && race.raceData[0].value !== '0'
        ? `${race.raceData[0].value} ${race.raceData[0].unit}`
        : ''

    const nameElements = [
      `${formattedStartDate}${
        formattedStartDate ? ' ' : ''
      }${displayStartTime}`,
      displayDistance,
      displayElevation
    ].filter(Boolean)

    const descriptionElements = [
      formattedStartDate ? `${t('event:startDate')} ${formattedStartDate}` : '',
      displayStartTime ? `${t('event:startHour')} ${displayStartTime}` : '',
      displayDistance ? `${t('event:raceLength')} ${displayDistance}` : '',
      displayElevation
        ? `${t('event:positiveElevation')} ${displayElevation}`
        : '',
      race.price ? `${t('event:bibPrice')} ${race.price} ${currency}` : ''
    ].filter(Boolean)

    idJsonObject.itemListElement.push({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'SportsEvent',
        '@context': 'https://schema.org',
        name: race.name,
        startDate: race.startDate,
        endDate,
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        location: {
          '@type': 'Place',
          name: city,
          address: {
            '@type': 'PostalAddress',
            addressLocality: city,
            addressCountry: 'FR'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: latitude,
            longitude: longitude
          }
        },
        ...(images.length > 0 && {
          image: images
        }),
        ...(descriptionElements.length > 0 && {
          description: descriptionElements.join(', ')
        }),
        offers: {
          type: 'Offer',
          url: `${basePath}/event/${slug}`,
          ...(race.price && { price: race.price, priceCurrency: currency }),
          name: `${nameElements.join(' - ')} (${
            race?.type ? t(race.type).toLowerCase() : ''
          })`,
          availability: 'https://schema.org/InStock',
          priceValidUntil: race.startDate
        },
        organizer: {
          '@type': 'Organization',
          name: organizer,
          url: organizerUrl
        }
      }
    })
  })

  return (
    <Script
      id="event-script"
      type="application/ld+json"
      strategy="beforeInteractive"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(idJsonObject) }}
    />
  )
}
